import { createContext, useContext } from 'react'
import type { Edge, Node } from '@xyflow/react'
import type { ExecutionRecord } from '@/services/api/retrostock-api'
import type { TradeRunSettings } from '@/features/backtest/utils/trade-run'

/**
 * Running the strategy open in Build, for the surfaces that offer it: the
 * Run Settings node's Run button (Simple Trade's and Advanced Trade's until
 * they are converted) and the Runs tab. The editor provides it
 * (hooks/use-trade-run.ts); outside the editor (the node gallery, docs) there
 * is none, and Run shows as unavailable.
 */
export interface TradeRunState {
  /** Why Run is unavailable, most fundamental first. Empty means it can run. */
  blockers: string[]
  /** Ticker days the connected universe resolves to. */
  tickerDayCount: number
  /**
   * The plan's ticker days per run when it is below the resolve cap and this
   * run reached it, so the scan may hold more than the run takes: the Run
   * confirmation says so (Chris, 2026-11-02). Null otherwise.
   */
  planTickerCap?: number | null
  /** The connected Stock Selection's name; empty when it has none. */
  universeName: string
  /** Graph-engine readiness errors; any is also a blocker. */
  readinessErrorCount: number
  isResolving: boolean
  isSubmitting: boolean
  settings: TradeRunSettings
  /** Save the strategy, launch it on the universe, and show the run in the Runs tab. */
  run: () => void
  /**
   * The graph Run would launch, read when called rather than held, so a drag
   * does not rebuild this state. The Run confirmation's summary reads it.
   */
  graph: () => { nodes: Node[]; edges: Edge[] }
  /** The newest run, launched or fetched, for the Run Settings node's last-run summary. */
  latestRun: ExecutionRecord | null
  /** Open the Runs tab on one run. */
  showRun: (executionId: string) => void
  /** Open one run's report. */
  openReport?: (executionId: string) => void
}

export const TradeRunContext = createContext<TradeRunState | null>(null)

export function useTradeRunState(): TradeRunState | null {
  return useContext(TradeRunContext)
}
